/* eslint-disable @typescript-eslint/no-unused-vars */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import React, { useContext, useEffect, useState } from 'react'
import { Avatar, Button, Input, Typography, Form, notification } from 'antd'
import { UserContext } from '../../core/user-context/user-context-middleware'
import { IProfileItems, checkPhoneNumber, decryptIdToken } from '../../pages/constants'
import { InputNum } from '../../common'
import { globalConfig } from '../../configuration/config'
import { API_URLs } from '../../configuration/api-urls'
import { CheckCircleFilled, CloseCircleOutlined } from '@ant-design/icons'

const { Text } = Typography

interface ProfilePageFormProps {
  onClose: () => void
  isVisible?: boolean
  data: IProfileItems | undefined
  updatePhoneNumber: (newPhoneNumber: string) => void
}

const ProfilePageForm: React.FC<ProfilePageFormProps> = ({
  onClose,
  isVisible,
  data,
  updatePhoneNumber,
}) => {
  const userDetails = useContext(UserContext)
  const { userDisplayName, userEmail, userPhoto, userInitials } = userDetails
  const initialPhoneNumber = data?.peerContactNumber || ''
  const [formChanged, setFormChanged] = useState(false)

  const [form] = Form.useForm()

  const [idToken, setIdToken] = useState<string | null>(null)

  useEffect(() => {
    const fetchDecryptedToken = async () => {
      const decryptedToken = await decryptIdToken();
      setIdToken(decryptedToken);
    };

    fetchDecryptedToken();
  }, []);

  const handleFormChange = () => {
    setFormChanged(true)
  }

  const openNotification = (status: boolean) => {
    notification.open({
      message: (
        <div className="text-lg font-medium">
          {status ? 'Success' : 'Failed'}
        </div>
      ),
      description: status
        ? 'Profile details saved successfully'
        : 'Failed to save profile details.',
      icon: status ? (
        <CheckCircleFilled className="ant-circleoutlined text-[#52c41a]" />
      ) : (
        <CloseCircleOutlined className="ant-circleoutlined text-red" />
      ),
      style: {
        background: status
          ? 'linear-gradient(to right, #52c41a 6%, white 6%)'
          : 'linear-gradient(to right, red 6%, white 6%)',
      },
    })
  }

  const patchProfileFetchUrl = globalConfig
    .get()
    .apiBaseUrl.concat(API_URLs.v1.PATCH_Profile)

  const handleSaveChanges = () => {
    const currentContactNumber = form.getFieldValue('phoneNumber')

    if (
      currentContactNumber !== undefined &&
      currentContactNumber.toString() !== initialPhoneNumber
    ) {
      form
        .validateFields(['phoneNumber'])
        .then(() => {
          const formattedContactNumber = currentContactNumber.toString()
          const payload = {
            peerContactNumber: formattedContactNumber,
          }

          fetch(patchProfileFetchUrl, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify(payload),
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
              }
              return response.json()
            })
            .then(() => {
              const updatedProfileDetails = {
                peerContactNumber: formattedContactNumber,
              }
              form.setFieldsValue(updatedProfileDetails)
              onClose()
              updatePhoneNumber(formattedContactNumber)
              openNotification(true)
            })
            .catch((error) => {
              console.log(error)
              openNotification(false)
            })
        })
        .catch((error) => {
          console.error('Form validation error:', error)
        })
    } else {
      onClose()
    }
  }

  return (
    <Form
      form={form}
      layout="horizontal"
      scrollToFirstError
      className="form-container space-y-2"
      onValuesChange={handleFormChange}
    >
      <div className="flex flex-row gap-10 pl-6 pt-6">
        <div>
          {userPhoto ? (
            <Avatar size={175} src={userPhoto} className="hidden md:block" />
          ) : (
            <Avatar size={175} className="bg-white-600 hidden md:block">
              {userInitials}
            </Avatar>
          )}
        </div>
        <div className="flex flex-col w-[75%] gap-6">
          <div className="flex flex-col space-y-2">
            <label className="text-[#969393]">Name</label>
            <Input
              defaultValue={data?.peerName || userDisplayName?.toString()}
              disabled
              className="md:max-w-[85%]"
            ></Input>
          </div>
          <div className="flex flex-col space-y-2">
            <label className="text-[#969393]">Email Id</label>
            <Input
              defaultValue={data?.peerEmail || userEmail?.toString()}
              disabled
              className="md:max-w-[85%]"
            ></Input>
          </div>
          <div className="flex flex-col space-y-2">
            <InputNum
              label={'Phone no. '}
              name={'phoneNumber'}
              isPhoneNumber={true}
              isRequired={true}
              validator={checkPhoneNumber}
              defaultValue={Number(data?.peerContactNumber) || ''}
              isProfilePage={true}
              className="w-full md:w-[92.3%] h-[2.25rem]"
            />
          </div>
          <div className="md:space-x-4 space-y-4 md:space-y-2">
            <Button
              className="!rounded-md w-[100%] md:w-[40%] focus:!border-green-600 hover:drop-shadow-sm hover:border-green-500 disabled:bg-gray-200 !important"
              onClick={handleSaveChanges}
              disabled={!formChanged}
            >
              Save changes
            </Button>
            <Button
              className="rounded-md !bg-white !text-[#969393] w-[100%] md:w-[25%]"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Form>
  )
}

export default ProfilePageForm
